/**
 * What a document pane is SHOWING, resolved against one project's catalog.
 *
 * Lifted out of `WorldShell` for RFC 0281 S2, and the lift is the point. It
 * used to close over the window's single project document, which is the same
 * assumption the ambient `workspaceId` prop was: one catalog, therefore one
 * project, therefore a window that cannot show two. A pure function of (catalog,
 * id) can be called once per pane, against whichever project that pane names.
 *
 * It is also the only part of the pane path worth testing directly. The rules
 * below are four different reasons an id resolves to something other than the
 * obvious document, and each was a bug before it was a rule.
 *
 * WHY `resolveSubject` AND NOT `resolveOpenFile`, which is what this was called.
 * It opens nothing — it is a pure lookup, and naming it for the side effect its
 * caller might go on to have made it read like an action. Worse, "file" is a
 * word RFC 0281 §4 spends a section taking back: files never become documents,
 * and a document returned as an `OpenFile` is that distinction leaking into the
 * one place it most needs to hold. A leaf names a SUBJECT — the vocabulary the
 * content tree already uses — and this resolves it.
 *
 * KNOWN LIMIT, and the reason to revisit rather than extend: the options bag.
 * `webFileFromId` and `canEmbedWeb` are there because web pages needed two
 * things a document did not, so every new content kind adds another parameter
 * and another branch below. That does not scale, and the shape that does is a
 * registry keyed by content kind, each kind owning its own resolve — the same
 * door RFC 0284 S3 wants built-ins to come through. Not worth doing for three
 * kinds; worth doing before the fifth.
 */

import {
  type AppSummary,
  type DocumentRef,
  installedApps,
  type ProjectDoc,
  projectDocuments,
} from '@mod/core/model'
import { branchPaneOf, branchSubject } from '../../features/branches/branchPane'
import {
  historyProjectOf,
  historySubject,
} from '../../features/projectHistory/historyPane'
import { codeFileFromId } from '../../services/code/codeFile'
import type { Subject } from './subject'

/**
 * The catalog a resolution reads: one project's apps and documents.
 *
 * A structural type rather than `ProjectDoc` itself, so a test states the two
 * lists it cares about instead of constructing a whole project document, and so
 * the caller decides how to project them (a hydrated doc, a cache, a stub).
 */
export interface ProjectCatalog {
  apps: AppSummary[]
  documents: DocumentRef[]
}

/** Read a project document into the two lists this needs. The World's apps
 *  count as the project's (RFC 0338 3g), so a record whose app is installed
 *  there opens. */
export const catalogOf = (
  doc: ProjectDoc,
  worldDoc?: unknown,
): ProjectCatalog => ({
  apps: installedApps(worldDoc, doc),
  documents: projectDocuments(doc),
})

/**
 * Can one of this app's records be opened, and listed, on its own?
 *
 * ONE PREDICATE, EXPORTED, because two places ask it and they must not
 * disagree: this file decides whether a pane can show an instance, and
 * `isBrowsableDocument` decides whether the sidebar and ⌘T will name one. An
 * answer that differs between them produces the worst of both — a record listed
 * everywhere and openable nowhere, or one that opens only from a link nothing
 * shows you.
 *
 * A `document` app qualifies because that is what the word means (RFC 0271). An
 * `application` qualifies only when it says so, because most of them should
 * not: a calendar event and a CRM row are rows in an index their app draws, and
 * listing each beside the project's documents would bury the documents.
 */
export function instancesAreOpenable(app: AppSummary): boolean {
  return app.presentation === 'document' || app.instancesOpenable === true
}

/**
 * Resolve an id against a project's catalog.
 *
 * `null` means "not a place to go", which the caller renders as nothing rather
 * than as an error. Four rules, in order:
 *
 *   1. A WEB PAGE carries its url in its id, so it resolves with no catalog at
 *      all, and before one is consulted: a page must survive a refresh in a
 *      project document that has not synced yet. Only where one can be RENDERED
 *      though, which is why `canEmbedWebPages` is a parameter and not an import
 *      here: a `web:` link opened in a browser resolves to nothing rather than
 *      mounting a viewer that can only show blank.
 *   2. An APP resolves by its own id OR by its current view document, so a link
 *      written before a republish still opens the app instead of pointing at a
 *      superseded artifact (RFC 0274). The app's own id is what comes back, so
 *      the open file survives its view being rebuilt.
 *   3. An INSTANCE opens as itself, drawn by its app's view (RFC 0271), when
 *      the app says its instances are openable — either because it is a
 *      `document` app, where that is the whole meaning of the word, or because
 *      it declared `instancesOpenable` while keeping its own index. Its own
 *      mime is irrelevant, since the instance is structured data rather than
 *      html, so the view's mime decides how it renders.
 *   4. Any other instance resolves to null. Those open inside their app, not as
 *      a pane of their own: a calendar event or a CRM record is data its app
 *      indexes, and a pane per row is not a thing anyone asked for.
 */
export function resolveSubject(
  catalog: ProjectCatalog | null,
  id: string | null,
  opts: {
    webFileFromId: (id: string) => Subject | null
    canEmbedWeb: boolean
  },
): Subject | null {
  if (!id) return null

  const web = opts.canEmbedWeb ? opts.webFileFromId(id) : null
  if (web) return web
  // A project file (RFC 0277 S6). Self-describing like a page, and needs no
  // catalog, but also no host capability: every host can fetch text.
  const code = codeFileFromId(id)
  if (code) return code
  // A project's History (RFC 0313 3i): it carries its project in its id.
  const history = historyProjectOf(id)
  if (history) return historySubject(history)
  // A branch's Compare pane (RFC 0313 3k), likewise.
  const branch = branchPaneOf(id)
  if (branch) return branchSubject(branch.projectId, branch.name)
  if (!catalog) return null

  const app = catalog.apps.find((a) => a.id === id || a.viewDocId === id)
  if (app) {
    return {
      id: app.id,
      appId: app.id,
      title: app.name,
      path: app.name,
      mime: 'text/html',
      viewDocId: app.viewDocId,
    }
  }

  const doc = catalog.documents.find((d) => d.id === id)
  if (!doc) return null

  if (doc.dataType) {
    const owner = catalog.apps.find((a) => a.dataType === doc.dataType)
    if (owner && instancesAreOpenable(owner)) {
      return {
        id: doc.id,
        appId: owner.id,
        title: doc.title,
        path: doc.path,
        mime: 'text/html',
        viewDocId: owner.viewDocId,
      }
    }
    // No app for its type at all (RFC 0338 S7): it opens to a prompt to
    // install one, so a record is never unreachable. An app that HAS it but
    // keeps it private still answers null, as rule 4 says.
    if (!owner) {
      return {
        id: doc.id,
        title: doc.title,
        path: doc.path,
        mime: doc.mime,
        missingApp: doc.dataType,
      }
    }
    return null
  }

  return { id: doc.id, title: doc.title, path: doc.path, mime: doc.mime }
}
